import CloseIcon from '@mui/icons-material/Close'; // ✅ Added for clean closing icon
import FullscreenIcon from '@mui/icons-material/Fullscreen'; // ✅ Added for Fullscreen feature
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'; // ✅ Added for Fullscreen feature
import MenuIcon from '@mui/icons-material/Menu';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined'; // ✅ Import preview icon selection

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react'; // ✅ Integrated useRef core hook
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Onedataicon } from '../Assets/Onedataicon.svg';
// ✅ Added Router hooks
import { useNavigate, useParams } from 'react-router-dom';
import PowerBiError from '../../pages/PowerBiReport/PowerBiError';
import PowerBIReport from '../../pages/PowerBiReport/PowerBiReport';
import {
  resetReportPreload,
  setPreloadPlan,
  setReportLoadStatus,
} from '../../redux/reportPreloadSlice';
import {
  addEmbedToken,
  addReportEmbedUrls,
  addSelectedReport,
  clearReportSlice,
} from '../../redux/userReducer';
import { getReportAccessAll } from '../Utilities/apis/getReportAccess';
import { userReportsActivity } from '../Utilities/apis/loginActivity';
import {
  checkvalidEmbedToken,
  reportEmbedTokenRefresh,
} from '../Utilities/checkValidToken';
import { getTopReports } from '../Utilities/v2Apis/getUserReports';
// ✅ Import slug functions
import { lookupReportRedirect } from '../Utilities/apis/reportRedirectApi';
import pbiMark from '../Utilities/pbiTiming';
import { findReportByName, generateSlug } from '../Utilities/slugify';
import classes from './CustomNavStyles.module.css'; // Keeping this for internal sidebar styling if needed
import SideBarComponent from './SideBarComponent/SideBarComponent';

// ✅ Added imports to enable background data fetching for the admin dropdown panel
import { getUsers } from '../../redux/adminReducer';
import { getAllUsersRefactoredApi } from '../Utilities/apis/getAllUsers';

const drawerWidth = 260;
const LAST_WORKSPACE_KEY = 'onedata:last-workspace';
// How many reports are kept loaded at once (the visible one + reports loaded
// in the background or opened earlier). Each Power BI iframe uses roughly
// 100-250 MB of browser memory, so the limit follows the device's memory
// (navigator.deviceMemory, in GB; not available in Firefox/Safari).
const MAX_KEPT_REPORTS = (() => {
  const gb =
    typeof navigator !== 'undefined' ? navigator.deviceMemory : undefined;
  if (!gb) return 4;
  if (gb >= 8) return 6;
  if (gb >= 4) return 4;
  return 2;
})();

// Runs fn when the browser is idle (or after a delay where that API is missing).
const whenIdle = (fn, timeout) =>
  'requestIdleCallback' in window
    ? { idle: true, id: window.requestIdleCallback(fn, { timeout }) }
    : { idle: false, id: window.setTimeout(fn, Math.min(timeout, 2000)) };
const cancelIdle = (handle) => {
  if (!handle) return;
  if (handle.idle) window.cancelIdleCallback(handle.id);
  else window.clearTimeout(handle.id);
};

const CustomNav = ({
  reportsArray: ownReportsArray,
  reportsLoading: ownReportsLoading = false,
  googleIdToken,
}) => {
  // ✅ Router setup
  const navigate = useNavigate();
  const { groupName, reportName } = useParams();

  // --- Redux & State ---
  const embedToken = useSelector((state) => state.PowerBi.reportEmbedToken);
  const embedUrls = useSelector((state) => state.PowerBi.reportEmbedUrls);
  const defaultReport = useSelector((state) => state.PowerBi.defaultReport);
  const selectedReport = useSelector((state) => state.PowerBi.selectedReport);
  const allPages = useSelector((state) => state.PowerBi.allPages);
  const currentPage = useSelector((state) => state.PowerBi.currentPage);
  const user = useSelector((state) => state.PowerBi.user);

  // ✅ Pulls the email details directly from Redux automatically
  const userList =
    useSelector((state) => state.AdminPageData.usersDetails) || [];

  const [embedReportError, setEmbedReportError] = useState(false);
  const [subReportsSent, setSubReportsSent] = useState(false);

  // ✅ Local tracking states for Admin Impersonation
  const [targetEmail, setTargetEmail] = useState(user?.EMAILID || '');
  const [inputEmail, setInputEmail] = useState('');

  // ✅ State to track full details of the user being impersonated by the admin
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  // The all-users list names the ID field `userId`; other sources use `ID`.
  const impersonatedUserIdRaw =
    impersonatedUser?.userId ?? impersonatedUser?.ID;

  // "View as": the sidebar stays the admin's own list; only the embed token
  // (identity / data filter) is the viewed user's. Just the report on screen
  // is loaded: no background loading, no kept reports.
  const impersonatedId =
    impersonatedUserIdRaw && impersonatedUserIdRaw !== user?.ID
      ? impersonatedUserIdRaw
      : null;
  const viewingAsOther =
    String(targetEmail || '')
      .trim()
      .toLowerCase() !==
    String(user?.EMAILID || '')
      .trim()
      .toLowerCase();
  const reportsArray = ownReportsArray;
  const reportsLoading = ownReportsLoading;

  // ✅ State changed to false so the admin search bar is hidden and ONLY the icon shows initially
  const [showAdminBar, setShowAdminBar] = useState(false);

  // ✅ State to track the temporary notification visibility
  const [showRefreshMessage, setShowRefreshMessage] = useState(false);

  // ✅ Native state engine hooks tracking active window presentation scale
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const mainContentRef = useRef(null);

  // --- Responsive State ---
  const theme = useTheme();
  // Returns true if screen is smaller than 'md' (900px)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const dispatch = useDispatch();

  // Falls back to the last known workspace so the token call doesn't have to
  // wait for the reports list; it re-runs if the real workspace differs.
  // When viewing as another user, the report on screen may live in a different
  // workspace than the admin's default report, so use its workspace.
  const workspaceId = impersonatedId
    ? selectedReport?.PBIWORKSPACEID || defaultReport?.PBIWORKSPACEID
    : defaultReport?.PBIWORKSPACEID ||
      (() => {
        try {
          return localStorage.getItem(LAST_WORKSPACE_KEY) || undefined;
        } catch (e) {
          return undefined;
        }
      })();

  // Embed tokens are issued per workspace, and the user's reports can sit in
  // several workspaces (e.g. a UAT section). Request every workspace the user
  // has reports in and merge the results, so opening a report from another
  // workspace never replaces the tokens of the reports already loaded. Until
  // the list is known only the primary workspace is used (fast start). When
  // viewing as someone else, only the workspace of the report on screen.
  const workspaceIds = (() => {
    const clean = (id) => (typeof id === 'string' ? id.trim() : id);
    const ids = new Set();
    if (workspaceId) ids.add(clean(workspaceId));
    if (!impersonatedId) {
      for (const [, groupReports] of reportsArray || []) {
        for (const r of groupReports) {
          if (r?.PBIWORKSPACEID) ids.add(clean(r.PBIWORKSPACEID));
        }
      }
    }
    return [...ids].sort();
  })();
  const workspaceIdsKey = workspaceIds.join(',');
  const workspaceIdsRef = useRef(workspaceIds);
  workspaceIdsRef.current = workspaceIds;

  // Guards the old-link redirect lookup so it runs once per unknown URL.
  const redirectChecked = useRef(null);

  // ✅ Uses your exact direct logic from the Header component
  const isAdminUser = user?.ISADMIN;

  // --- Handlers ---

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleEmbedError = () => {
    setEmbedReportError(true);
  };
  const actualGroupName = React.useMemo(() => {
    if (!selectedReport?.PBIREPORTID || !reportsArray) return groupName;
    for (const [gName, gReports] of reportsArray) {
      if (gReports.some((r) => r.PBIREPORTID === selectedReport.PBIREPORTID)) {
        return gName; // Returns the exact string, e.g., "US+"
      }
    }
    return groupName;
  }, [selectedReport, reportsArray, groupName]);

  // ✅ Fullscreen Trigger Action Handler
  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      mainContentRef.current?.requestFullscreen().catch((err) => {
        console.error(
          `Error enabling full-screen mode calculation execution: ${err.message}`,
        );
      });
    } else {
      document.exitFullscreen();
    }
  };

  // ✅ Admin Form Submission Handler
  const handleFetchReportForEmail = (e) => {
    e.preventDefault();
    if (inputEmail.trim()) {
      // Changing targetEmail drops all kept reports (see the report slots
      // section), so the next report loads fresh for the new identity.
      const normalizedEmail = inputEmail.trim();
      setTargetEmail(normalizedEmail);

      // ✅ Find the matching full user object based on the inputted email string
      const matchedUser = userList.find(
        (u) => u?.EMAILID?.toLowerCase() === normalizedEmail.toLowerCase(),
      );

      if (matchedUser) {
        setImpersonatedUser(matchedUser);
      } else {
        setImpersonatedUser(null);
      }
    }
  };

  const handleReports = (report) => {
    // ✅ 1. Find the group name for this report
    let targetGroupName = 'general';
    for (const [gName, gReports] of reportsArray) {
      if (gReports.some((r) => r.PBIREPORTID === report.PBIREPORTID)) {
        targetGroupName = gName;
        break;
      }
    }

    // ✅ 2. Generate slugs and Navigate to the new URL
    const groupSlug = generateSlug(targetGroupName);
    const reportSlug = generateSlug(report.ReportName);

    navigate(`/reports/${groupSlug}/${reportSlug}`);

    // If on mobile, close the drawer automatically after selection
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Only the most recent token request may update state. A slower, older
  // response (e.g. for a previous workspace or impersonated user) is ignored.
  const tokenRequestSeq = useRef(0);

  // Whose tokens are currently in Redux. Reports only embed when this matches
  // the current identity, so after an admin switches "view as" user nothing is
  // embedded with the previous user's token while the new one is loading.
  const normalizeEmail = (email) =>
    String(email || '')
      .trim()
      .toLowerCase();
  const currentIdentity = normalizeEmail(targetEmail || user?.EMAILID);
  const [tokenIdentity, setTokenIdentity] = useState(null);

  const applyReportAccess = (result, identityEmail) => {
    if (result.length > 1) {
      setTokenIdentity(normalizeEmail(identityEmail));
      dispatch(addReportEmbedUrls(result[1]));
      dispatch(addEmbedToken(result[0]));
      // result[0] is a { reportId: token } map; the timer needs a token string.
      reportEmbedTokenRefresh(
        refreshEmbedToken,
        Object.values(result[0] || {})[0],
      );
      setEmbedReportError(false);
    } else {
      setEmbedReportError(true);
    }
  };

  // force = skip the client and server caches (used when a report is missing
  // from the cached token map, e.g. access was granted a moment ago).
  const refreshEmbedToken = async (force = false) => {
    const requestId = ++tokenRequestSeq.current;
    const identityEmail = targetEmail || user?.EMAILID;
    const result = await getReportAccessAll(
      workspaceIdsRef.current,
      impersonatedUser?.PBI_ROLE || user?.PBI_ROLE, // ✅ Pass dynamic role from selected user
      identityEmail, // ✅ Pass dynamic targetEmail here
      impersonatedUserIdRaw || user?.ID, // ✅ Pass dynamic ID from selected user
      {
        force: force === true,
        // Admin "view as": include the report on screen even if the viewed
        // user has no access to it.
        extraReportId: viewingAsOther ? selectedReport?.PBIREPORTID : '',
      },
    );
    if (requestId !== tokenRequestSeq.current) return;
    applyReportAccess(result, identityEmail);
  };

  // --- UseEffects ---

  // ✅ Synchronizes device window state mappings directly to the internal state triggers on hardware updates
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsNativeFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // ✅ Background Initializer Hook: Loads users instantly if Redux is empty on reports page load
  useEffect(() => {
    if (isAdminUser && userList.length === 0) {
      getAllUsersRefactoredApi().then((data) => {
        if (data && data.length > 0) {
          dispatch(getUsers(data));
        }
      });
    }
  }, [isAdminUser, userList.length, dispatch]);

  // ✅ Sync input targets when user profile state securely hydrates from the API
  useEffect(() => {
    if (user?.EMAILID) {
      setTargetEmail(user.EMAILID);
      setInputEmail(user.EMAILID);
      setImpersonatedUser(null); // Reset impersonation context when base admin user changes
    }
  }, [user?.EMAILID]);

  // ✅ CENTRAL TRACKING HOOK: records a report open for the usage metrics.
  // Counted: the user clicking a report in the sidebar, or arriving on a
  // report through the URL. Not counted:
  //  - reports loading in the background (they never become the selected
  //    report, only a hidden iframe);
  //  - anything while an admin is viewing as another user (the selection then
  //    follows the viewed user's list, and the opens aren't the admin's use);
  //  - the automatic re-selection right after "view as" ends.
  const lastLoggedReportRef = useRef(null);
  const viewAsEndedAtRef = useRef(0);
  const wasViewingAsRef = useRef(false);
  useEffect(() => {
    if (wasViewingAsRef.current && !viewingAsOther) {
      viewAsEndedAtRef.current = Date.now();
    }
    wasViewingAsRef.current = viewingAsOther;
  }, [viewingAsOther]);
  useEffect(() => {
    const reportId = selectedReport?.reportId;
    if (!user?.ID || !reportId) return;
    if (viewingAsOther) {
      // Remember it so it isn't counted when the admin stops viewing as.
      lastLoggedReportRef.current = reportId;
      return;
    }
    if (Date.now() - viewAsEndedAtRef.current < 3000) {
      lastLoggedReportRef.current = reportId;
      return;
    }
    if (lastLoggedReportRef.current === reportId) return;
    lastLoggedReportRef.current = reportId;
    userReportsActivity([user.ID, reportId]);
  }, [selectedReport?.reportId, user?.ID, viewingAsOther]);

  // ✅ New UseEffect: Handle Report Selection from URL
  useEffect(() => {
    if (!reportsArray || reportsArray.length === 0) return;

    let reportToSelect = null;

    // 1. Check URL parameters first
    if (groupName && reportName) {
      reportToSelect = findReportByName(reportsArray, groupName, reportName);

      // 1b. Unknown slugs: maybe an old bookmark. Ask the backend for a configured
      // redirect (admin panel -> Redirects) before falling back to the default.
      if (!reportToSelect) {
        const oldPath = `/reports/${groupName}/${reportName}`;
        if (redirectChecked.current !== oldPath) {
          redirectChecked.current = oldPath;
          lookupReportRedirect(oldPath).then((newUrl) => {
            if (newUrl) {
              navigate(newUrl, { replace: true });
            } else if (Object.keys(defaultReport).length > 0) {
              dispatch(addSelectedReport(defaultReport));
            }
          });
        }
        return;
      }
    }

    // 2. Fallback to default report if URL doesn't match
    if (!reportToSelect && Object.keys(defaultReport).length > 0) {
      reportToSelect = defaultReport;
    }

    // 3. Dispatch to Redux if the report changed
    if (
      reportToSelect &&
      reportToSelect.PBIREPORTID !== selectedReport?.PBIREPORTID
    ) {
      dispatch(addSelectedReport(reportToSelect));
    }
  }, [
    groupName,
    reportName,
    reportsArray,
    defaultReport,
    dispatch,
    selectedReport,
  ]);

  // Report selection and report-level usage tracking are kept.
  // Subreport-level activity storage has been intentionally removed to avoid
  // unnecessary writes and improve rendering speed.

  // Default Report Selection (Preserved but now mostly handled by the new URL logic)
  useEffect(() => {
    // URLs with group/report slugs are resolved by the URL effect above (incl. old
    // bookmarks -> redirect), so don't select (and log usage for) the default here.
    if (groupName && reportName) return;
    if (
      Object.keys(selectedReport).length === 0 &&
      Object.keys(defaultReport).length > 0
    ) {
      dispatch(addSelectedReport(defaultReport));
    }
  }, [defaultReport, dispatch, selectedReport, groupName, reportName]);

  // Remember the last workspace so the embed token request can start right
  // away on the next load, without waiting for the reports list API.
  useEffect(() => {
    if (defaultReport?.PBIWORKSPACEID) {
      try {
        localStorage.setItem(LAST_WORKSPACE_KEY, defaultReport.PBIWORKSPACEID);
      } catch (e) {
        // storage unavailable, ignore
      }
    }
  }, [defaultReport?.PBIWORKSPACEID]);

  // 4. Initial Embed Token
  // Waits for a valid Google token (PrivateRoutes refreshes it after the user
  // returns following a long idle period); otherwise the request goes out with
  // an expired token, fails, and leaves the reports blank.
  const lastTokenKey = useRef(null);
  useEffect(() => {
    const intialembedTokenSetting = async () => {
      const requestId = ++tokenRequestSeq.current;
      pbiMark('token requested');
      const result = await getReportAccessAll(
        workspaceIds,
        impersonatedUser?.PBI_ROLE || user?.PBI_ROLE, // ✅ Pass dynamic role parameter
        targetEmail, // ✅ Dynamic Target email parameter
        impersonatedUserIdRaw || user?.ID, // ✅ Pass dynamic user ID parameter
      );
      if (requestId !== tokenRequestSeq.current) return;
      pbiMark('token received');
      applyReportAccess(result, targetEmail);
    };
    if (workspaceId && targetEmail && checkvalidEmbedToken(googleIdToken)) {
      const key = [
        workspaceIdsKey,
        targetEmail,
        impersonatedUserIdRaw || user?.ID,
      ].join('|');
      // Skip if we already fetched for this exact context and it's still valid
      // (the Google token refreshing hourly shouldn't re-request embed tokens).
      const existing = Object.values(embedToken || {})[0];
      if (lastTokenKey.current === key && checkvalidEmbedToken(existing))
        return;
      lastTokenKey.current = key;
      intialembedTokenSetting();
    }
    // ✅ Added impersonatedUser as dependency loop listener
  }, [
    workspaceId,
    workspaceIdsKey,
    dispatch,
    targetEmail,
    user?.ID,
    user?.PBI_ROLE,
    impersonatedUser,
    googleIdToken,
  ]);

  // When the tab comes back after a long time, recover from an expired embed
  // token. PrivateRoutes refreshes the Google token first; if it is still
  // expired here, the effect above re-runs once googleIdToken changes.
  useEffect(() => {
    const onVisible = () => {
      const id = selectedReport?.PBIREPORTID;
      if (
        document.visibilityState === 'visible' &&
        id &&
        Object.keys(embedToken || {}).length > 0 &&
        !checkvalidEmbedToken(embedToken[id])
      ) {
        lastTokenKey.current = null;
        setEmbedReportError(false);
        if (checkvalidEmbedToken(localStorage.getItem('googleIdToken'))) {
          refreshEmbedToken(true);
        }
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport?.PBIREPORTID, embedToken]);

  // If the selected report is missing from a loaded token map (report access
  // granted after the token was cached), fetch a fresh token once per report.
  const forcedRefreshFor = useRef(new Set());
  useEffect(() => {
    const id = selectedReport?.PBIREPORTID;
    if (
      !id ||
      !workspaceId ||
      !targetEmail ||
      // Wait until the tokens of the CURRENT identity have arrived. (Checking
      // for a non-empty token map instead meant a viewed user with no reports
      // in the workspace, i.e. an empty map, never got the report fetched.)
      tokenIdentity === null ||
      tokenIdentity !== currentIdentity ||
      embedToken[id] ||
      forcedRefreshFor.current.has(id)
    ) {
      return;
    }
    forcedRefreshFor.current.add(id);
    refreshEmbedToken(true).then(() => setCheckedMissingFor(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedReport?.PBIREPORTID,
    embedToken,
    workspaceId,
    targetEmail,
    tokenIdentity,
  ]);

  // Set once a fresh token has been fetched and the selected report is still
  // not in it, i.e. the user being viewed has no access to this report.
  const [checkedMissingFor, setCheckedMissingFor] = useState(null);
  const [accessNotice, setAccessNotice] = useState('');

  // --- Report slots (keep-alive + warm spare) ---
  // Each slot is one mounted PowerBIReport, i.e. one Power BI iframe.
  // - A slot with reportId === null is the "spare": an iframe already warmed
  //   up with powerbi.bootstrap(), waiting for the next report that hasn't
  //   been opened yet. One spare exists from page load.
  // - Once a slot gets a report it keeps that report for its whole life (we
  //   never re-embed a different report into a used iframe; doing that while
  //   it is still loading makes the Power BI SDK throw).
  // - Opened reports stay mounted but hidden, so switching back is instant.
  //   At most MAX_KEPT_REPORTS are kept; the least recently used is removed.
  // - A new spare is only created after the visible report has rendered, so
  //   it never competes with the report the user is waiting for.
  const slotSeq = useRef(0);
  const makeSlot = (reportId = null, rank = 0) => ({
    slotId: `pbi-slot-${++slotSeq.current}`,
    reportId,
    lastUsed: Date.now(),
    // Position in the keep list: lower = more important. When the list is
    // full the highest rank (the "6th") is the one dropped.
    rank,
  });
  const [slots, setSlots] = useState(() => [makeSlot()]);
  // Position of each report in the background-loading plan (set below), and a
  // counter that ranks reports opened by a click after the planned ones.
  const planRankRef = useRef(new Map());
  const clickSeq = useRef(0);

  // Remove the "region hint" saved by an earlier version (no longer used).
  useEffect(() => {
    try {
      localStorage.removeItem('onedata:pbi-bootstrap-url');
    } catch (e) {
      // storage unavailable, ignore
    }
  }, []);

  // Only embed once the report to show is certain, otherwise the report saved
  // from the last visit can start loading and then be thrown away when the
  // list picks a different one. It is certain when:
  // - the reports list has loaded, or
  // - (refresh / shared link) the URL names a report and it is the same report
  //   that was saved from the last visit. Then there is no need to wait for
  //   the list. Access is still enforced: a report the user can no longer see
  //   has no token, so it never embeds.
  const reportsListLoaded =
    Array.isArray(reportsArray) && reportsArray.length > 0;
  const selectedReportId = selectedReport?.PBIREPORTID;
  const urlMatchesSavedReport = Boolean(
    reportName &&
    selectedReportId &&
    selectedReport?.ReportName &&
    generateSlug(selectedReport.ReportName) === reportName,
  );
  const selectionConfirmed = reportsListLoaded || urlMatchesSavedReport;

  // How often this user opens each report (report IDs and counts only, kept in
  // localStorage per user). Background loading goes most-used first.
  const usageKey = user?.ID ? `onedata:report-usage:${user.ID}` : null;
  const usageRef = useRef(null);
  if (usageRef.current === null || usageRef.current.key !== usageKey) {
    let counts = {};
    try {
      counts =
        (usageKey && JSON.parse(localStorage.getItem(usageKey) || '{}')) || {};
    } catch (e) {
      counts = {};
    }
    usageRef.current = { key: usageKey, counts };
  }
  useEffect(() => {
    // Opens while viewing as someone else aren't the admin's own use.
    if (!usageKey || !selectedReportId || viewingAsOther) return;
    const counts = usageRef.current.counts;
    counts[selectedReportId] = (counts[selectedReportId] || 0) + 1;
    try {
      localStorage.setItem(usageKey, JSON.stringify(counts));
    } catch (e) {
      // storage unavailable, ignore
    }
  }, [usageKey, selectedReportId]);

  // Identity change ("view as" another user): drop every kept report so no
  // report embedded with the previous user's token stays in the page.
  const [identityEpoch, setIdentityEpoch] = useState(0);
  const lastIdentityRef = useRef(targetEmail);
  useEffect(() => {
    if (lastIdentityRef.current === targetEmail) return;
    lastIdentityRef.current = targetEmail;
    setSlots([makeSlot()]);
    // Slots were just emptied: make the selected report claim a new one even
    // if it is the same report as before (otherwise nothing loads until the
    // user clicks another report).
    setIdentityEpoch((n) => n + 1);
    // Slicer selections saved for the previous user's reports must not be
    // applied to the next user's copy of the same report (they can leave it
    // blank, e.g. a value the new user's data doesn't contain).
    dispatch(clearReportSlice());
    // Drop the previous user's embed tokens/URLs too, so nothing of theirs can
    // be embedded (or left showing) while the new user's tokens load.
    dispatch(addEmbedToken({}));
    dispatch(addReportEmbedUrls({}));
    lastTokenKey.current = null;
    // New identity: allow the "missing report" check to run again, clear any
    // old error, and drop the old identity's tokens so nothing stale is used.
    forcedRefreshFor.current = new Set();
    setCheckedMissingFor(null);
    setAccessNotice('');
    setEmbedReportError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetEmail]);

  // "View as" a user who can't open the selected report: it would wait for a
  // token that never comes. Switch to the first report that user can open.
  useEffect(() => {
    const id = selectedReportId;
    // When viewing as a user, a report they can't open shows the "couldn't be
    // loaded" message; the admin picks another report from their own sidebar.
    if (impersonatedId) return;
    if (
      !id ||
      checkedMissingFor !== id ||
      !tokensMatchIdentity ||
      embedToken[id]
    ) {
      return;
    }
    for (const [, groupReports] of reportsArray || []) {
      const accessible = groupReports.find(
        (r) => r.PBIREPORTID !== id && embedToken[r.PBIREPORTID],
      );
      if (accessible) {
        setAccessNotice(
          `${targetEmail} doesn't have access to this report. Showing ${accessible.ReportName} instead.`,
        );
        handleReports(accessible);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkedMissingFor,
    selectedReportId,
    tokenIdentity,
    embedToken,
    reportsArray,
  ]);

  // Give the selected report a slot: reuse its kept slot, or take the spare.
  useEffect(() => {
    if (!selectedReportId || !selectionConfirmed) return;
    setSlots((prevAll) => {
      // Viewing as someone else: keep only the report on screen (plus the
      // spare), so switching reports replaces it instead of piling them up.
      const prev = viewingAsOther
        ? prevAll.filter(
            (s) => s.reportId === null || s.reportId === selectedReportId,
          )
        : prevAll;
      const now = Date.now();
      if (prev.some((s) => s.reportId === selectedReportId)) {
        return prev.map((s) =>
          s.reportId === selectedReportId
            ? { ...s, lastUsed: now, opened: true }
            : s,
        );
      }
      // A report from the top of the plan keeps its plan position. Any other
      // report the user opens takes the last (6th) position, replacing the
      // previous last one.
      const planned = planRankRef.current.get(selectedReportId);
      const rank =
        planned !== undefined && planned < MAX_KEPT_REPORTS
          ? planned
          : planRankRef.current.size === 0
            ? 0
            : 1000 + ++clickSeq.current;
      const spare = prev.find((s) => s.reportId === null);
      let next = spare
        ? prev.map((s) =>
            s === spare
              ? {
                  ...s,
                  reportId: selectedReportId,
                  lastUsed: now,
                  rank,
                  opened: true,
                }
              : s,
          )
        : [...prev, { ...makeSlot(selectedReportId, rank), opened: true }];
      const used = next.filter((s) => s.reportId !== null);
      if (used.length > MAX_KEPT_REPORTS) {
        // Drop reports the user never opened first, from the end of the plan
        // (6th, then 5th, then 4th, ...), so each newly opened report takes the
        // next position up. Only when every kept report has been opened does
        // the least recently used one go.
        const evict = used
          .filter((s) => s.reportId !== selectedReportId)
          .sort((a, b) => {
            if (!!a.opened !== !!b.opened) return a.opened ? 1 : -1;
            return a.opened
              ? a.lastUsed - b.lastUsed
              : b.rank - a.rank || a.lastUsed - b.lastUsed;
          })
          .slice(0, used.length - MAX_KEPT_REPORTS)
          .map((s) => s.slotId);
        next = next.filter((s) => !evict.includes(s.slotId));
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReportId, selectionConfirmed, identityEpoch]);

  // Called by a report once it has rendered: prepare a new spare, but only
  // when the browser is idle, so it doesn't compete with a report the user
  // has just opened.
  const spareTimerRef = useRef(null);
  const ensureSpareSlot = React.useCallback(() => {
    if (spareTimerRef.current) return;
    const addSpare = () => {
      spareTimerRef.current = null;
      setSlots((prev) =>
        prev.some((s) => s.reportId === null) ? prev : [...prev, makeSlot()],
      );
    };
    spareTimerRef.current =
      'requestIdleCallback' in window
        ? window.requestIdleCallback(addSpare, { timeout: 5000 })
        : window.setTimeout(addSpare, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(
    () => () => {
      if (!spareTimerRef.current) return;
      if ('cancelIdleCallback' in window) {
        window.cancelIdleCallback(spareTimerRef.current);
      } else {
        window.clearTimeout(spareTimerRef.current);
      }
    },
    [],
  );

  const findReportById = (id) => {
    if (selectedReport?.PBIREPORTID === id) return selectedReport;
    for (const [, groupReports] of reportsArray || []) {
      const found = groupReports.find((r) => r.PBIREPORTID === id);
      if (found) return found;
    }
    return null;
  };

  const tokensMatchIdentity =
    tokenIdentity !== null && tokenIdentity === currentIdentity;

  // Embed URL + token for a slot's report, or nothing if not ready yet.
  const getSlotEmbed = (reportId) => {
    if (!reportId || !tokensMatchIdentity) return null;
    const url = embedUrls[reportId];
    const token = embedToken[reportId];
    return url && checkvalidEmbedToken(token) ? { url, token } : null;
  };

  // Which slot is on screen: the selected report's slot, once it has started
  // embedding (it has its URL + token). Until then nothing is shown and the
  // warm spare keeps starting up out of sight. Showing the spare earlier made
  // Power BI appear to start twice: its logo, a blank page while waiting for
  // the report, then loading again.
  const activeSlot = slots.find(
    (s) => s.reportId !== null && s.reportId === selectedReportId,
  );
  const visibleSlotId =
    activeSlot && getSlotEmbed(activeSlot.reportId) ? activeSlot.slotId : null;
  // While waiting (report list / token), show the same small spinner the
  // original app showed while the report list loaded.
  // A fresh token was fetched and still has no entry for the selected report:
  // it will never embed, so say so instead of spinning forever.
  const missingTokenForSelected =
    !visibleSlotId &&
    Boolean(selectedReportId) &&
    checkedMissingFor === selectedReportId &&
    tokensMatchIdentity &&
    !embedToken[selectedReportId];

  const showWaitingSpinner =
    !visibleSlotId &&
    !missingTokenForSelected &&
    (reportsLoading || Boolean(selectedReportId));

  // --- Background loading of the other reports, one by one ---
  // Order: the reports this user opens most first, then sidebar order.
  // Only reports the user has a token for (same
  // workspace as the default report). Stops at MAX_KEPT_REPORTS; the rest
  // load when opened. The header shows the progress (ReportPreloadStatus).
  const loadStatusById = useSelector((state) => state.reportPreload.statusById);
  // The viewed user's most-opened reports from the server's usage data (the
  // signed-in user's own, or whoever an admin is viewing as). Background
  // loading follows this order, so their top reports are ready first.
  const topReportsOwnerId = user?.ID;
  const [topReports, setTopReports] = useState({ ownerId: null, ids: [] });
  useEffect(() => {
    if (!topReportsOwnerId) return undefined;
    let cancelled = false;
    getTopReports(undefined, 6).then((rows) => {
      if (cancelled) return;
      setTopReports({
        ownerId: topReportsOwnerId,
        ids: rows.map((r) => r.PBIREPORTID).filter(Boolean),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [topReportsOwnerId]);
  const topRank = new Map(
    (topReports.ownerId === topReportsOwnerId ? topReports.ids : []).map(
      (id, i) => [id, i],
    ),
  );
  // No background loading while viewing as someone else.
  const preloadPlan = [];
  if (!viewingAsOther) {
    const seen = new Set();
    for (const [sectionName, groupReports] of reportsArray || []) {
      for (const r of groupReports) {
        if (!r?.PBIREPORTID || seen.has(r.PBIREPORTID)) continue;
        seen.add(r.PBIREPORTID);
        if (getSlotEmbed(r.PBIREPORTID)) {
          preloadPlan.push({
            id: r.PBIREPORTID,
            name: r.ReportName,
            section: sectionName,
          });
        }
      }
    }
  }
  {
    const counts = usageRef.current?.counts || {};
    const sidebarIndex = new Map(preloadPlan.map((r, i) => [r.id, i]));
    preloadPlan.sort(
      (a, b) =>
        (topRank.has(a.id) ? topRank.get(a.id) : 99) -
          (topRank.has(b.id) ? topRank.get(b.id) : 99) ||
        (counts[b.id] || 0) - (counts[a.id] || 0) ||
        sidebarIndex.get(a.id) - sidebarIndex.get(b.id),
    );
  }
  const preloadPlanKey = preloadPlan.map((r) => r.id).join('|');
  planRankRef.current = new Map(preloadPlan.map((r, i) => [r.id, i]));

  // Publish the plan for the header indicator.
  useEffect(() => {
    dispatch(setPreloadPlan({ plan: preloadPlan, maxKept: MAX_KEPT_REPORTS }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadPlanKey, dispatch]);

  // Leaving the reports page removes every iframe: clear the indicator.
  useEffect(() => () => dispatch(resetReportPreload()), [dispatch]);

  const handleReportStatus = React.useCallback(
    (id, status) => dispatch(setReportLoadStatus({ id, status })),
    [dispatch],
  );

  const preloadTimerRef = useRef(null);
  useEffect(() => {
    if (!reportsListLoaded || !tokensMatchIdentity || !selectedReportId) return;
    if (viewingAsOther) return;
    // (Background loading needs the full list, so it still waits for it.)
    if (preloadTimerRef.current) return;

    const planIds = new Set(preloadPlan.map((r) => r.id));
    const isDone = (st) => st === 'ready' || st === 'slow' || st === 'error';

    // 1. The report the user is looking at comes first.
    if (
      planIds.has(selectedReportId) &&
      !isDone(loadStatusById[selectedReportId])
    ) {
      return;
    }
    // 2. One report at a time: wait while any report is loading, including
    //    one just added whose status hasn't arrived yet.
    const used = slots.filter((s) => s.reportId !== null);
    const busy = used.some(
      (s) => planIds.has(s.reportId) && !isDone(loadStatusById[s.reportId]),
    );
    if (busy) return;
    // 3. Stop at the memory limit.
    if (used.length >= MAX_KEPT_REPORTS) return;

    const next = preloadPlan.find(
      (r) => !used.some((s) => s.reportId === r.id),
    );
    if (!next) return;

    preloadTimerRef.current = whenIdle(() => {
      preloadTimerRef.current = null;
      setSlots((prev) => {
        const prevUsed = prev.filter((s) => s.reportId !== null);
        if (
          prevUsed.some((s) => s.reportId === next.id) ||
          prevUsed.length >= MAX_KEPT_REPORTS
        ) {
          return prev;
        }
        return [
          ...prev,
          makeSlot(next.id, planRankRef.current.get(next.id) ?? 0),
        ];
      });
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    slots,
    loadStatusById,
    selectedReportId,
    reportsListLoaded,
    tokensMatchIdentity,
    preloadPlanKey,
  ]);
  useEffect(() => () => cancelIdle(preloadTimerRef.current), []);

  // --- Render ---

  // Common content for the sidebar
  const drawerContent = (
    <Box
      className={classes.sidebar}
      sx={{
        height: '100%', // Fill the drawer
        backgroundColor: '#312e2d',
        display: 'flex', // Enable flexbox
        flexDirection: 'column', // Stack items vertically
        overflow: 'hidden', // Prevent the parent from scrolling
      }}
    >
      <SideBarComponent
        reports={reportsArray}
        handleReports={handleReports}
        loading={reportsLoading}
      />
    </Box>
  );

  return (
    <StyledEngineProvider injectFirst>
      <Box sx={{ display: 'flex' }}>
        {/* Mobile Menu Button (Hamburger) */}
        {isMobile && !isNativeFullscreen && (
          <IconButton
            color='inherit'
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}
            sx={{
              position: 'fixed',
              top: '70px', // Adjust based on your Header height
              left: '15px',
              zIndex: 2,
              color: '#f7901d',
              backgroundColor: 'rgba(49, 46, 45, 0.8)',
              '&:hover': {
                backgroundColor: 'rgba(49, 46, 45, 1)',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Navigation / Sidebar Area */}
        <Box
          component='nav'
          sx={{
            width: { md: isNativeFullscreen ? 0 : drawerWidth },
            flexShrink: { md: 0 },
            transition: 'width 0.2s ease',
          }}
        >
          {isMobile ? (
            // --- Mobile Drawer (Temporary) ---
            <Drawer
              variant='temporary'
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }} // Better open performance on mobile
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': {
                  boxSizing: 'border-box',
                  width: drawerWidth,
                  backgroundColor: '#312e2d',
                  borderRight: '1px solid #ffffff1a',
                },
              }}
            >
              {drawerContent}
            </Drawer>
          ) : (
            // --- Desktop Sidebar (Permanent) ---
            !isNativeFullscreen && (
              <Drawer
                variant='permanent'
                sx={{
                  display: { xs: 'none', md: 'block' },
                  '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: drawerWidth,
                    backgroundColor: '#312e2d',
                    borderRight: '1px solid #ffffff1a',

                    // 1. DEFAULT SETTING (For widths > 1280px or fallback)
                    marginTop: '60px',
                    height: 'calc(100vh - 60px)',

                    // 2. RANGE: 900px to 1024px
                    '@media (min-width: 900px) and (max-width: 1024px)': {
                      marginTop: '40px',
                      height: 'calc(100vh - 40px)',
                    },

                    // 3. RANGE: Just above 1024px to 1280px
                    '@media (min-width: 1024px) and (max-width: 1280px)': {
                      marginTop: '50px',
                      height: 'calc(100vh - 50px)',
                    },

                    '@media (min-width: 1620px)': {
                      marginTop: '120px',
                      height: 'calc(100vh - 120px)',
                    },
                  },
                }}
                open
              >
                {drawerContent}
              </Drawer>
            )
          )}
        </Box>

        {/* ✅ Main Content Area */}
        <Box
          ref={mainContentRef} // Anchored to capture 100% display canvas scaling on full screen toggle executions
          component='main'
          sx={{
            p: 0,
            width: {
              md: isNativeFullscreen ? '100%' : `calc(100% - ${drawerWidth}px)`,
            },
            mt: isNativeFullscreen ? 0 : '1px',
            overflow: 'hidden', // Prevents double scrollbars
          }}
        >
          {/* ✅ ENHANCED: Fullscreen Status Header Overlay Panel (Background color maintained perfectly) */}
          {isNativeFullscreen && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: '60px',
                backgroundColor: '#312e2d',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                zIndex: 9999,
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.4)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  color: '#ffffff',
                  flexWrap: 'nowrap',
                  minWidth: 0,
                  overflow: 'hidden',
                  height: '100%', // ✅ Matches the 60px header height explicitly
                }}
              >
                {/* Brand Identity / Icon Wrapper Layout */}
                {/* Brand Identity / Icon Wrapper Layout */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center', // Force absolute vertical centering
                    justifyContent: 'center', // Clean horizontal structural alignment
                    flexShrink: 0,
                    height: '100%', // Inherit the full 60px header height explicitly
                    '& svg': {
                      marginLeft: '8px',
                      display: 'block',
                      height: '65%', // Optimized scale so it centers beautifully without layout crowding
                      width: 'auto', // Keeps native aspect ratio pristine
                    },
                  }}
                >
                  <Onedataicon style={{ color: '#f7901d' }} />
                </Box>
                {/* Subheader tracking context bundled inside a unified single-line row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'nowrap',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  {/* Workspace / Group Slugs Track */}
                  <Box
                    sx={{
                      textTransform: 'capitalize',
                      fontSize: '0.88rem',
                      fontWeight: 500,
                      letterSpacing: '0.4px',
                      color: 'rgba(255, 255, 255, 0.7)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {actualGroupName || 'General'}
                  </Box>

                  {/* Path Separator */}
                  <Box
                    component='span'
                    sx={{
                      opacity: 0.3,
                      fontWeight: 300,
                      fontSize: '1.2rem',
                      userSelect: 'none',
                    }}
                  >
                    /
                  </Box>

                  {/* Precise Active Resource Unit Context */}
                  <Box
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.92rem',
                      letterSpacing: '0.4px',
                      color: '#ffffff',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {selectedReport?.ReportName || reportName}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }} />
            </Box>
          )}

          {/* High-End Floating Admin Bar Overlay Panel ("View as" Feature) */}
          {isAdminUser && showAdminBar && (
            <Box
              component='form'
              onSubmit={handleFetchReportForEmail}
              sx={{
                position: 'absolute',
                bottom: '80px',
                right: '24px',
                zIndex: 10,
                display: 'flex',
                gap: 1.5,
                height: '42px',
                p: '0 14px',
                backgroundColor: '#131212',
                borderRadius: '10px',
                alignItems: 'center',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.65)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                opacity: 0.25,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  opacity: 0.95,
                },
              }}
            >
              {/* Searchable Autocomplete Dropdown Selection */}
              <Autocomplete
                freeSolo
                openOnFocus
                forcePopupIcon={true}
                options={
                  userList ? userList.map((row) => row?.EMAILID || row) : []
                }
                value={inputEmail}
                onChange={(event, newValue) => {
                  setInputEmail(newValue || '');
                }}
                onInputChange={(event, newInputValue) => {
                  setInputEmail(newInputValue);
                }}
                componentsProps={{
                  paper: {
                    sx: {
                      backgroundColor: '#131212',
                      color: '#ffffff',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.6)',
                      borderRadius: '10px',
                      marginTop: '8px',
                      fontSize: '0.85rem',
                      '& .MuiAutocomplete-listbox': {
                        padding: '6px',
                        '&::-webkit-scrollbar': { width: '5px' },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          borderRadius: '10px',
                        },
                      },
                      '& .MuiAutocomplete-option': {
                        borderRadius: '6px',
                        padding: '8px 14px',
                        transition: 'all 0.15s ease',
                        marginBottom: '2px',
                        '&:hover': {
                          backgroundColor: 'rgba(247, 144, 29, 0.08)',
                          color: '#f7901d',
                        },
                        '&[aria-selected="true"]': {
                          backgroundColor: 'rgba(247, 144, 29, 0.15)',
                          color: '#f7901d',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: 'rgba(247, 144, 29, 0.22)',
                          },
                        },
                      },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Impersonate user email...'
                    variant='outlined'
                    size='small'
                    sx={{
                      width: 290,
                      '& .MuiOutlinedInput-root': {
                        height: '30px',
                        fontSize: '0.82rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)',
                        borderRadius: '6px',
                        color: '#ffffff',
                        padding: '0 4px 0 10px !important',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(247, 144, 29, 0.4)',
                        },
                        '@media (min-width: 900px)': {
                          borderColor: 'rgba(255, 255, 255, 0.12)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#f7901d',
                          borderWidth: '1px',
                        },
                      },
                      '& .MuiOutlinedInput-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.35)',
                        opacity: 1,
                      },
                      '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator':
                        {
                          color: 'rgba(255, 255, 255, 0.4)',
                        },
                    }}
                  />
                )}
              />
              <Button
                type='submit'
                variant='contained'
                size='small'
                sx={{
                  height: '30px',
                  px: 2.5,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#f7901d',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#d47713', boxShadow: 'none' },
                }}
              >
                Load Report
              </Button>

              {/* Minimal Clean Cross Closure Panel Control Toggle */}
              <IconButton
                onClick={() => {
                  setShowAdminBar(false);
                }}
                size='small'
                sx={{
                  color: 'rgba(255, 255, 255, 0.35)',
                  marginLeft: 0.5,
                  padding: '4px',
                  '&:hover': {
                    color: '#f7901d',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <CloseIcon style={{ fontSize: '1rem' }} />
              </IconButton>
            </Box>
          )}

          {/* Floating Admin Preview Trigger Icon ("View as" Icon) */}
          {isAdminUser && !showAdminBar && !isNativeFullscreen && (
            <IconButton
              onClick={() => setShowAdminBar(true)}
              sx={{
                position: 'absolute',
                bottom: '80px',
                right: '24px',
                zIndex: 11,
                backgroundColor: '#161515',
                color: '#f7901d',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.5)',
                padding: '10px',
                opacity: 0.25,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  opacity: 0.95,
                  backgroundColor: '#22201f',
                  transform: 'scale(1.05)',
                  boxShadow: '0px 8px 25px rgba(247, 144, 29, 0.2)',
                },
              }}
            >
              <PreviewOutlinedIcon style={{ fontSize: '1.4rem' }} />
            </IconButton>
          )}

          {/* Floating Fullscreen Premium Button Widget ("Zoom" Feature) */}
          <IconButton
            onClick={handleToggleFullscreen}
            sx={{
              position: 'absolute',
              bottom: '24px',
              right: '24px',
              zIndex: 11,
              backgroundColor: '#161515',
              color: '#f7901d',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.5)',
              padding: '10px',
              opacity: 0.25,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                opacity: 0.95,
                backgroundColor: '#22201f',
                transform: 'scale(1.05)',
                boxShadow: '0px 8px 25px rgba(247, 144, 29, 0.2)',
              },
            }}
          >
            {isNativeFullscreen ? (
              <FullscreenExitIcon style={{ fontSize: '1.4rem' }} />
            ) : (
              <FullscreenIcon style={{ fontSize: '1.4rem' }} />
            )}
          </IconButton>

          {/* Wrapper container */}
          <Box
            sx={{
              width: '100%',
              flexGrow: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              ...(isNativeFullscreen && {
                '& .PowerBiReport': {
                  marginTop: {
                    xs: '60px !important',
                    md: '42px !important',
                    lg: '50px !important',
                    xl: '60px !important',
                  },
                  height: {
                    xs: 'calc(100vh - 60px) !important',
                    md: 'calc(100vh - 42px) !important',
                    lg: 'calc(100vh - 50px) !important',
                    xl: 'calc(100vh - 60px) !important',
                  },
                  width: '100% !important',
                },
              }),
            }}
          >
            {/* Report conditional rendering blocks remain intact */}
            {!embedReportError ? (
              // All slots are rendered; only one is visible. The others (kept,
              // loading in the background, or the warm spare) are stacked under
              // it. zIndex 0 keeps their stacking inside this box.
              <Box
                key={identityEpoch}
                sx={{ position: 'relative', zIndex: 0, width: '100%' }}
              >
                {slots.map((slot) => {
                  const slotEmbed = getSlotEmbed(slot.reportId);
                  return (
                    <PowerBIReport
                      key={slot.slotId}
                      selectedReport={
                        slot.reportId ? findReportById(slot.reportId) : null
                      }
                      embedUrl={slotEmbed?.url}
                      embedToken={slotEmbed?.token}
                      isActive={
                        slot.reportId !== null &&
                        slot.reportId === selectedReportId
                      }
                      isVisible={slot.slotId === visibleSlotId}
                      onRendered={ensureSpareSlot}
                      onStatusChange={handleReportStatus}
                      handleEmbedError={handleEmbedError}
                    />
                  );
                })}
                {missingTokenForSelected && (
                  <div
                    className='PowerBiReport'
                    role='alert'
                    style={{
                      position: 'relative',
                      zIndex: 1,
                      background: '#ffffff',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      gap: 8,
                      padding: 24,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: '#312e2d',
                      }}
                    >
                      This report couldn't be loaded
                    </div>
                    <div style={{ fontSize: 14, color: '#6b6866' }}>
                      No access token was issued for{' '}
                      {selectedReport?.ReportName} for {targetEmail}.
                    </div>
                    <Button
                      onClick={() => {
                        forcedRefreshFor.current.delete(selectedReportId);
                        setCheckedMissingFor(null);
                        refreshEmbedToken(true);
                      }}
                      sx={{ color: '#f7901d' }}
                    >
                      Retry
                    </Button>
                  </div>
                )}
                {showWaitingSpinner && (
                  <div
                    className='PowerBiReport'
                    role='status'
                    aria-label='Loading report'
                    style={{
                      position: 'relative',
                      zIndex: 1,
                      background: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress size={32} style={{ color: '#f7901d' }} />
                  </div>
                )}
              </Box>
            ) : (
              <PowerBiError refreshEmbedToken={refreshEmbedToken} />
            )}
          </Box>
        </Box>
      </Box>
      <Dialog open={Boolean(accessNotice)} onClose={() => setAccessNotice('')}>
        <DialogTitle>Report access</DialogTitle>
        <DialogContent>
          <DialogContentText>{accessNotice}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccessNotice('')} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </StyledEngineProvider>
  );
};

export default CustomNav;
